import React, { useState } from "react";
import {
  formatSurfaceTotals,
  formatSurfaceType,
  workTypeLabel,
} from "../../../helpers/estimates";
import { formatMeasurementArea, formatMeasurementDimensions } from "../../../helpers/measurementModel";
import {
  computeLabourLineTotal,
  computeMaterialLineTotal,
  computeWorkTypeTotal,
  formatCurrency,
  formatLabourLineDetail,
  getMaterialUnitPrice,
} from "../../../helpers/estimateTotals";
import {
  PANEL_DIVIDER_STYLE,
  PANEL_TITLE_STYLE,
  SUBTOTAL_STYLE,
  TOTAL_CARD_STYLE,
  WORK_TYPE_CARD_STYLE,
} from "../estimateStyles";
import {
  DOC_ITEM_AMOUNT,
  DOC_ITEM_BLOCK,
  DOC_ITEM_META,
  DOC_ITEM_TITLE,
} from "./estimateReviewStyles";

function formatMaterialLine(row, materialsCatalog) {
  const quantity =
    row.quantity != null && row.quantity !== "" ? Number(row.quantity) : null;
  const unitCost = getMaterialUnitPrice(row, materialsCatalog);
  const unitLabel = row.unit_of_measure_snapshot
    ? ` / ${row.unit_of_measure_snapshot}`
    : "";

  if (quantity == null) {
    return unitCost > 0 ? `${formatCurrency(unitCost)}${unitLabel}` : "";
  }

  return `${quantity} × ${formatCurrency(unitCost)}${unitLabel}`;
}

function MeasurementsBlock({ workType }) {
  const sections = workType.sections || [];
  const surfaceTotals = formatSurfaceTotals(workType.surface_totals);

  if (sections.length === 0 && surfaceTotals.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "16px" }}>
      <div style={PANEL_TITLE_STYLE}>Measurements</div>
      <div style={PANEL_DIVIDER_STYLE} />

      {surfaceTotals.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontWeight: 600, fontSize: "13px", color: "#c9a84c", marginBottom: "6px" }}>
            Net Area
          </div>
          {surfaceTotals.map((row) => (
            <div key={row.surfaceType} style={DOC_ITEM_META}>
              {row.label}: {row.sqft.toFixed(2)} sqft
            </div>
          ))}
        </div>
      )}

      {sections.length === 0 ? (
        <div style={DOC_ITEM_META}>No measurements recorded.</div>
      ) : (
        sections.map((section) => (
          <div key={section.section_id} style={DOC_ITEM_BLOCK}>
            <div style={DOC_ITEM_TITLE}>{section.name}</div>
            <div style={DOC_ITEM_META}>
              {section.record_kind === "deduction" ? "Deduction" : "Area"}
              {" · "}
              {formatSurfaceType(section.surface_type)}
              {section.record_kind === "deduction" &&
                section.applies_to_surface_type &&
                ` → deducts from ${formatSurfaceType(section.applies_to_surface_type)}`}
            </div>
            <div style={DOC_ITEM_META}>
              {formatMeasurementDimensions(section)} = {formatMeasurementArea(section)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function MaterialsBlock({ workType, materialsCatalog }) {
  const rows = workType.materials || [];
  const total = rows.reduce(
    (sum, row) => sum + computeMaterialLineTotal(row, materialsCatalog),
    0,
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "16px" }}>
      <div style={PANEL_TITLE_STYLE}>Materials</div>
      <div style={PANEL_DIVIDER_STYLE} />

      {rows.map((row) => {
        const detail = formatMaterialLine(row, materialsCatalog);
        const lineTotal = computeMaterialLineTotal(row, materialsCatalog);

        return (
          <div key={row.room_material_id} style={DOC_ITEM_BLOCK}>
            <div style={DOC_ITEM_TITLE}>{row.material_name_snapshot}</div>
            {detail && <div style={DOC_ITEM_META}>{detail}</div>}
            {row.notes && <div style={DOC_ITEM_META}>Notes: {row.notes}</div>}
            <div style={DOC_ITEM_AMOUNT}>= {formatCurrency(lineTotal)}</div>
          </div>
        );
      })}

      <div style={SUBTOTAL_STYLE}>
        <span style={{ fontWeight: 600 }}>Materials Subtotal</span>
        <span style={{ fontWeight: 700, color: "#c9a84c" }}>
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
}

function LabourBlock({ workType }) {
  const rows = workType.labour || [];
  const total = rows.reduce(
    (sum, row) => sum + computeLabourLineTotal(row, workType),
    0,
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "16px" }}>
      <div style={PANEL_TITLE_STYLE}>Labour</div>
      <div style={PANEL_DIVIDER_STYLE} />

      {rows.map((row) => {
        const lineTotal = computeLabourLineTotal(row, workType);

        return (
          <div key={row.labour_id} style={DOC_ITEM_BLOCK}>
            <div style={DOC_ITEM_TITLE}>{row.labour_activity}</div>
            <div style={DOC_ITEM_META}>
              {formatLabourLineDetail(row, workType)}
            </div>
            {row.notes && <div style={DOC_ITEM_META}>Notes: {row.notes}</div>}
            <div style={DOC_ITEM_AMOUNT}>= {formatCurrency(lineTotal)}</div>
          </div>
        );
      })}

      <div style={SUBTOTAL_STYLE}>
        <span style={{ fontWeight: 600 }}>Labour Subtotal</span>
        <span style={{ fontWeight: 700, color: "#c9a84c" }}>
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
}

export function WorkTypeReviewSection({ workType, materialsCatalog }) {
  const [expanded, setExpanded] = useState(true);

  const label = workTypeLabel(workType);
  const surfaceTotals = formatSurfaceTotals(workType.surface_totals);
  const primarySqft =
    surfaceTotals.length > 0
      ? surfaceTotals.reduce((sum, row) => sum + row.sqft, 0)
      : null;
  const workTypeTotal = computeWorkTypeTotal(workType, materialsCatalog);

  const metaParts = [
    workType.assembly_name_snapshot,
    primarySqft != null ? `${primarySqft.toFixed(1)} sqft` : null,
  ].filter(Boolean);

  return (
    <div style={WORK_TYPE_CARD_STYLE}>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 16px",
          background: "rgba(201,168,76,0.08)",
          border: "none",
          color: "inherit",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: "16px", color: "#e8dcc0" }}>
            {expanded ? "▲" : "▼"} {label}
          </div>
          {metaParts.length > 0 && (
            <div style={{ opacity: 0.7, fontSize: "13px", marginTop: "4px" }}>
              {metaParts.join(" · ")}
            </div>
          )}
        </div>
        {workTypeTotal > 0 && (
          <div style={{ fontWeight: 700, color: "#c9a84c", fontSize: "15px" }}>
            {formatCurrency(workTypeTotal)}
          </div>
        )}
      </button>

      {expanded && (
        <div style={{ padding: "16px" }}>
          {workType.assembly_name_snapshot && (
            <div style={{ ...DOC_ITEM_META, marginBottom: "12px" }}>
              Assembly: {workType.assembly_name_snapshot}
            </div>
          )}

          <MeasurementsBlock workType={workType} />
          <MaterialsBlock workType={workType} materialsCatalog={materialsCatalog} />
          <LabourBlock workType={workType} />

          {workTypeTotal > 0 && (
            <div style={{ ...TOTAL_CARD_STYLE, marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#c9a84c",
                }}
              >
                <span>Work Type Total</span>
                <span>{formatCurrency(workTypeTotal)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
